import ENDPOINTS from '@/config/apiEndpoints';
import APP_CONFIG from '@/config/app';
import {
  ErrorInterface,
  TokenInterface,
  YieldBearingAssetInterface,
} from '@/interfaces';
import { UnderlyingYieldBearingAssetInterface } from '@/interfaces/UnderlyingYieldBearingAssetInterface';
import ApiService, { handleUnauthorized } from '@/services/ApiService';
import { convertScientificToDecimalString } from '@/utils/formatUtils';
import { generateLogoUri } from '@/utils/functionalUtils';
import { logError, logInfo } from '@/utils/logger';
export const enrichTokensWithBlockchainDisplayName = (
  tokens: TokenInterface[],
): TokenInterface[] => {
  return tokens.map((token) => ({
    ...token,
    blockchainDisplayName:
      APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME[
        token.blockchain as keyof typeof APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME
      ] || token.blockchain,
  }));
};
const enrichUnderlyingAssetsWithBlockchainDisplayName = (
  underlyingAssets: UnderlyingYieldBearingAssetInterface[],
): UnderlyingYieldBearingAssetInterface[] => {
  return underlyingAssets.map((asset) => ({
    ...asset,
    blockchainDisplayName:
      APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME[
        asset.blockchain as keyof typeof APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME
      ] || asset.blockchain,
    logoUri: generateLogoUri(asset.symbol, asset.logoUri),
  }));
};
const fetch = async (): Promise<YieldBearingAssetInterface[]> => {
  try {
    const response = await ApiService.get(ENDPOINTS.YIELD_BEARING_ASSETS);
    if (response.isBetaRestricted) {
      logInfo(
        'Beta restriction encountered for yield bearing assets',
        'YieldBearingAssets.fetchYieldBearingAssets',
        response,
      );
      handleUnauthorized();
      return [];
    }
    if (Array.isArray(response)) {
      return await Promise.all(
        response.map(async ({ nativeAssets, ...rest }) => {
          const tokensSupportedForDeposit = Array.isArray(
            rest.tokensSupportedForDeposit,
          )
            ? enrichTokensWithBlockchainDisplayName(
                rest.tokensSupportedForDeposit,
              )
            : [];
          const tokensSupportedForWithdrawal = Array.isArray(
            rest.tokensSupportedForWithdrawal,
          )
            ? enrichTokensWithBlockchainDisplayName(
                rest.tokensSupportedForWithdrawal,
              )
            : [];
          const underlyingTokens = Array.isArray(rest.underlyingTokens)
            ? enrichTokensWithBlockchainDisplayName(rest.underlyingTokens)
            : [];
          const underlyingYieldBearingAssets = Array.isArray(nativeAssets)
            ? enrichUnderlyingAssetsWithBlockchainDisplayName(nativeAssets)
            : [];
          return {
            ...rest,
            tokensSupportedForDeposit,
            tokensSupportedForWithdrawal,
            underlyingTokens,
            underlyingYieldBearingAssets,
          };
        }),
      );
    } else {
      logError(
        'Unexpected response format, expected an array',
        'YieldBearingAssets.fetchYieldBearingAssets',
        response,
      );
      return [];
    }
  } catch (error: any) {
    logError(error, 'YieldBearingAssets.fetchYieldBearingAssets');
    if (error.isBetaRestricted) {
      logInfo(
        'Beta restriction encountered for yield bearing assets',
        'YieldBearingAssets.fetchYieldBearingAssets',
        error,
      );
      handleUnauthorized();
      return [];
    }
    throw new Error('Failed to fetch yield-bearing assets');
  }
};
const feeEstimateCache = new Map<
  string,
  { response?: any; timestamp?: number; inProgress?: Promise<any> }
>();
export const getDepositEstimate = async (
  ticker: string,
  yieldBearingNativeAssets: any[],
  depositToken: any,
  amount: string,
): Promise<{
  sliverFee: string;
  sliverFeeInUsd: string;
  gasCost: string;
  gasCostInUsd: string;
  bridgeFee: string;
  bridgeFeeInUsd: string;
  totalFee: string;
  totalFeeInUsd: string;
  isBetaRestricted?: boolean;
}> => {
  try {
    const normalizedAmount = convertScientificToDecimalString(amount);
    const cacheKey = JSON.stringify({
      ticker,
      yieldBearingNativeAssets: yieldBearingNativeAssets.map((asset) => ({
        id: asset.id,
        composition: Number(asset.composition).toFixed(2),
      })),
      depositToken: {
        symbol: depositToken.symbol,
        address: depositToken.address,
        blockchain: depositToken.blockchain,
      },
      amount: normalizedAmount,
    });
    const currentTime = Date.now();
    const CACHE_DURATION_MS = 10000;
    const cachedEntry = feeEstimateCache.get(cacheKey);
    if (cachedEntry?.inProgress) {
      logInfo(
        'Returning ongoing request promise for the same fee estimate.',
        'YieldBearingAssets.getDepositEstimate',
        { cacheKey },
      );
      return cachedEntry.inProgress;
    }
    if (
      cachedEntry?.response &&
      currentTime - (cachedEntry.timestamp || 0) < CACHE_DURATION_MS
    ) {
      logInfo(
        'Returning cached fee estimate.',
        'YieldBearingAssets.getDepositEstimate',
        { cacheKey, cachedResponse: cachedEntry.response },
      );
      return cachedEntry.response;
    }
    logInfo(
      'No valid cache entry found; initiating new API request.',
      'YieldBearingAssets.getDepositEstimate',
      { cacheKey },
    );
    const requestBody = {
      ticker,
      yieldBearingNativeAssets: yieldBearingNativeAssets.map((asset) => ({
        id: asset.id,
        composition: Number(asset.composition).toFixed(2),
      })),
      depositToken: {
        symbol: depositToken.symbol,
        address: depositToken.address,
        blockchain: depositToken.blockchain,
      },
      amount: normalizedAmount,
    };
    const inProgressRequest = ApiService.post(
      ENDPOINTS.YIELD_BEARING_ASSETS_DEPOSIT_ESTIMATE,
      requestBody,
    )
      .then((response) => {
        if (
          response &&
          typeof response === 'object' &&
          (response.error === true ||
            response.isServerError === true ||
            response.errorCode)
        ) {
          const errorDetails = {
            statusCode: response.statusCode || 500,
            errorCode: response.errorCode || 'SERVER_ERROR',
            errorMessage:
              response.errorMessage ||
              'An unexpected error occurred while fetching deposit estimate.',
            details: response.details || response.errorMessage,
          };
          logError(
            errorDetails,
            'YieldBearingAssets.getDepositEstimate.errorResponse',
          );
          throw errorDetails;
        }
        feeEstimateCache.set(cacheKey, {
          response,
          timestamp: Date.now(),
          inProgress: undefined,
        });
        logInfo(
          'Fee estimate fetched and cached successfully.',
          'YieldBearingAssets.getDepositEstimate',
          { cacheKey, response },
        );
        return response;
      })
      .catch((error) => {
        feeEstimateCache.delete(cacheKey);
        logError(error, 'YieldBearingAssets.getDepositEstimate');
        if (error.isBetaRestricted) {
          logInfo(
            'Beta restriction encountered for yield bearing asset deposit fee estimate',
            'YieldBearingAssets.getDepositEstimate',
            error,
          );
          handleUnauthorized();
          const betaRestrictedResponse = {
            sliverFee: '0',
            sliverFeeInUsd: '0',
            gasCost: '0',
            gasCostInUsd: '0',
            bridgeFee: '0',
            bridgeFeeInUsd: '0',
            totalFee: '0',
            totalFeeInUsd: '0',
            isBetaRestricted: true,
          };
          feeEstimateCache.set(cacheKey, {
            response: betaRestrictedResponse,
            timestamp: Date.now(),
            inProgress: undefined,
          });
          return betaRestrictedResponse;
        }
        const statusCode = error.response?.status || 500;
        const errorCode = error.response?.data?.errorCode || undefined;
        const errorMessage =
          error.response?.data?.errorMessage ||
          'An unexpected error occurred while fetching estimated deposit fees. Please try again later.';
        const details =
          error.response?.data?.details || errorMessage || error.message;
        const errorDetails: ErrorInterface = {
          statusCode,
          ...(errorCode && { errorCode }),
          errorMessage,
          details,
        };
        logError(errorDetails, 'YieldBearingAssets.getDepositEstimate');
        throw errorDetails;
      });
    feeEstimateCache.set(cacheKey, {
      inProgress: inProgressRequest,
    });
    return inProgressRequest;
  } catch (error: any) {
    logError(error, 'YieldBearingAssets.getDepositEstimate');
    if (error.isBetaRestricted) {
      logInfo(
        'Beta restriction encountered for yield bearing asset deposit fee estimate',
        'YieldBearingAssets.getDepositEstimate',
        error,
      );
      handleUnauthorized();
      return {
        sliverFee: '0',
        sliverFeeInUsd: '0',
        gasCost: '0',
        gasCostInUsd: '0',
        bridgeFee: '0',
        bridgeFeeInUsd: '0',
        totalFee: '0',
        totalFeeInUsd: '0',
        isBetaRestricted: true,
      };
    }
    throw new Error('Failed to fetch trade estimate');
  }
};
export const deposit = async (
  yieldBearingAssetId: string,
  yieldBearingNativeAssets: any[],
  depositToken: any,
  amount: string,
): Promise<any> => {
  try {
    const normalizedAmount = convertScientificToDecimalString(amount);
    const requestBody = {
      ticker: yieldBearingAssetId,
      yieldBearingNativeAssets: yieldBearingNativeAssets.map((asset) => ({
        id: asset.id,
        composition: Number(asset.composition).toFixed(2),
      })),
      depositToken: {
        symbol: depositToken.symbol,
        address: depositToken.address,
        blockchain: depositToken.blockchain,
      },
      amount: normalizedAmount,
    };
    const response = await ApiService.post(
      ENDPOINTS.YIELD_BEARING_ASSETS_DEPOSIT,
      requestBody,
    );
    if (
      response &&
      typeof response === 'object' &&
      (response.error === true ||
        response.isServerError === true ||
        response.errorCode)
    ) {
      const errorDetails = {
        statusCode: response.statusCode || 500,
        errorCode: response.errorCode || 'SERVER_ERROR',
        errorMessage:
          response.errorMessage ||
          'An unexpected error occurred while processing the deposit.',
        details: response.details || response.errorMessage,
      };
      logError(errorDetails, 'YieldBearingAssets.deposit.errorResponse');
      throw errorDetails;
    }
    return response;
  } catch (error: any) {
    logError(error, 'YieldBearingAssets.deposit');
    if (error.isBetaRestricted) {
      logInfo(
        'Beta restriction encountered for yield bearing asset deposit',
        'YieldBearingAssets.deposit',
        error,
      );
      handleUnauthorized();
      return {
        isBetaRestricted: true,
        message: 'This feature requires beta access',
      };
    }
    const statusCode = error.response?.status || 500;
    const errorCode = error.response?.data?.errorCode || undefined;
    const errorMessage =
      error.response?.data?.errorMessage ||
      'An unexpected error occurred while processing the deposit. Please try again later.';
    const details = error.response?.data?.details || error.message;
    throw {
      statusCode,
      ...(errorCode && { errorCode }),
      errorMessage,
      details,
    };
  }
};
export const submitRating = async (
  ticker: string,
  ratingType: 'assetQuality' | 'investmentExperience',
  rating: number,
): Promise<any> => {
  try {
    const endpoint = ENDPOINTS.YIELD_BEARING_ASSETS_SUBMIT_RATINGS.replace(
      '[yield_bearing_asset_ticker]',
      ticker,
    );
    const response = await ApiService.post(endpoint, { ratingType, rating });
    if (response.isBetaRestricted) {
      logInfo(
        'Beta restriction encountered for yield bearing asset rating submission',
        'YieldBearingAssets.submitRating',
        response,
      );
      handleUnauthorized();
      return {
        isBetaRestricted: true,
        message: 'This feature requires beta access',
      };
    }
    if (
      response &&
      typeof response === 'object' &&
      (response.error === true ||
        response.isServerError === true ||
        response.errorCode)
    ) {
      const errorDetails = {
        statusCode: response.statusCode || 500,
        errorCode: response.errorCode || 'SERVER_ERROR',
        errorMessage:
          response.errorMessage ||
          'An unexpected error occurred while submitting rating.',
        details: response.details || response.errorMessage,
      };
      logError(errorDetails, 'YieldBearingAssets.submitRating.errorResponse');
      throw errorDetails;
    }
    return response;
  } catch (error: any) {
    logError(error, 'YieldBearingAssets.submitRating');
    if (error.isBetaRestricted) {
      logInfo(
        'Beta restriction encountered for yield bearing asset rating submission',
        'YieldBearingAssets.submitRating',
        error,
      );
      handleUnauthorized();
      return {
        isBetaRestricted: true,
        message: 'This feature requires beta access',
      };
    }
    throw new Error('Failed to submit rating');
  }
};
export const optimizePortfolio = async (
  assetsWithData: Array<{
    name: string;
    timeSeriesData: Array<{ timestamp: number; apy: number; tvlUsd: number }>;
  }>,
  assessment: Array<{ question: string; rating: number }>,
  userCommentary?: string,
): Promise<{
  allocations: number[];
  explanation: string;
  isBetaRestricted?: boolean;
}> => {
  try {
    const response = await ApiService.post(
      ENDPOINTS.YIELD_BEARING_ASSETS_OPTIMIZE_PORTFOLIO,
      {
        assetsWithData,
        assessment,
        userCommentary,
      },
    );
    if (
      response &&
      typeof response === 'object' &&
      (response.error === true ||
        response.isServerError === true ||
        response.errorCode)
    ) {
      const errorDetails = {
        statusCode: response.statusCode || 500,
        errorCode: response.errorCode || 'SERVER_ERROR',
        errorMessage:
          response.errorMessage ||
          'An unexpected error occurred while optimizing portfolio.',
        details: response.details || response.errorMessage,
      };
      logError(
        errorDetails,
        'YieldBearingAssets.optimizePortfolio.errorResponse',
      );
      throw errorDetails;
    }
    if (
      !Array.isArray(response.allocations) ||
      typeof response.explanation !== 'string'
    ) {
      logError(
        'Invalid response format from portfolio optimization',
        'YieldBearingAssets.optimizePortfolio',
        response,
      );
      throw new Error('Invalid response format from portfolio optimization');
    }
    return {
      allocations: response.allocations,
      explanation: response.explanation,
    };
  } catch (error: any) {
    logError(error, 'YieldBearingAssets.optimizePortfolio');
    if (error.isBetaRestricted) {
      logInfo(
        'Beta restriction encountered for yield bearing asset portfolio optimization',
        'YieldBearingAssets.optimizePortfolio',
        error,
      );
      handleUnauthorized();
      return {
        isBetaRestricted: true,
        allocations: [],
        explanation: 'This feature requires beta access',
      };
    }
    throw error;
  }
};
export default fetch;
